import { useState, type ReactNode } from "react";
import type { ClientDetails } from "~/common/workflows/types/client";
import {
  expiryDisplay,
  likelihoodTone,
} from "~/common/workflows/components/ClientContextTile";

export interface PinnedHeaderProps {
  clientName: string | null;
  policyNumber: string | null;
  rightLabel: string | null;
  rightLabelTitle?: string;
  /** When provided, header gains a chevron expanding to the policies-in-force section. */
  clientDetails?: ClientDetails | null;
  /** True while details are in-flight or stale — shows skeleton instead of old data. */
  clientLoading?: boolean;
  clientDefaultExpanded?: boolean;
  /**
   * A control at the right end of the client/policy row — Open Email, the hop
   * back to the request the run started from. It belongs with the context, so
   * it is in the same place on every surface that shows this header.
   *
   * Rendered BESIDE the expand toggle, never inside it: a button inside a
   * button is invalid markup, and a click on it would also expand the header.
   */
  action?: ReactNode;
}

export function PinnedHeader({
  clientName,
  policyNumber,
  rightLabel,
  rightLabelTitle = "Step",
  clientDetails,
  clientLoading = false,
  clientDefaultExpanded = false,
  action,
}: PinnedHeaderProps) {
  const [expanded, setExpanded] = useState(clientDefaultExpanded);
  const canExpand = !!clientDetails || clientLoading;

  const activePolicies =
    clientDetails?.policies_in_force?.filter((p) => p.status === "active") ?? [];
  const opportunities = clientDetails?.opportunities ?? [];
  const totalPremium = activePolicies.reduce(
    (sum, p) => sum + (p.expiring_premium ?? 0),
    0,
  );

  const headerRow = (
    <div className="flex items-center gap-3 px-4 py-2 w-full text-left">
      {canExpand ? (
        clientLoading && !clientDetails ? (
          <span
            className="h-3 w-3 animate-spin rounded-full border border-primary-gray-light-300 border-t-sunrise flex-shrink-0"
            aria-hidden="true"
          />
        ) : (
          <span
            className="text-primary-gray-light-500 text-xs flex-shrink-0 transition-transform duration-200 leading-none"
            style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
            aria-hidden="true"
          >
            ▶
          </span>
        )
      ) : null}

      <div className="min-w-0">
        <p className="text-[9px] uppercase tracking-wide text-primary-gray-light-500">
          Client
        </p>
        <p className="text-xs font-semibold text-primary-gray-light-900 truncate">
          {clientName ?? "—"}
        </p>
      </div>
      <div className="flex-shrink-0">
        <p className="text-[9px] uppercase tracking-wide text-primary-gray-light-500">
          Policy
        </p>
        <p className="text-xs font-semibold text-primary-gray-light-900">
          {policyNumber ?? "—"}
        </p>
      </div>
      {rightLabel && (
        <div className="flex-shrink-0 ml-auto">
          <p className="text-[9px] uppercase tracking-wide text-primary-gray-light-500">
            {rightLabelTitle}
          </p>
          <p className="text-xs font-semibold text-primary-gray-light-900">
            {rightLabel}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex-shrink-0 bg-primary-gray-light-50 border-b border-primary-gray-light-200">
      <div className="flex items-center">
        <div className="min-w-0 flex-1">
          {canExpand ? (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="w-full hover:bg-primary-gray-light-100 transition-colors duration-150"
              aria-expanded={expanded}
              data-testid="pinned-header-toggle"
            >
              {headerRow}
            </button>
          ) : (
            headerRow
          )}
        </div>
        {action ? (
          <div className="flex-shrink-0 pr-4" data-testid="pinned-header-action">
            {action}
          </div>
        ) : null}
      </div>

      {/* CSS-Grid height animation: grid-rows transitions from 0fr → 1fr smoothly.
          Content stays mounted so the collapse animation plays on navigation. */}
      {canExpand && (
        <div
          className="grid transition-[grid-template-rows] duration-300 ease-in-out"
          style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="bg-base-white border-t border-primary-gray-light-200" data-testid="pinned-header-client-context">
              {clientLoading ? (
                // Skeleton sized for one policy row — keeps the panel from
                // collapsing to zero while the real data is in-flight.
                <div className="px-4 py-3 flex flex-col gap-2">
                  <div className="flex justify-between">
                    <div className="h-[9px] w-28 rounded bg-primary-gray-light-100 motion-safe:animate-pulse" />
                    <div className="h-[9px] w-16 rounded bg-primary-gray-light-100 motion-safe:animate-pulse" />
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="h-3 w-40 rounded bg-primary-gray-light-100 motion-safe:animate-pulse" />
                    <div className="h-3 w-14 rounded bg-primary-gray-light-100 motion-safe:animate-pulse" />
                  </div>
                </div>
              ) : (activePolicies.length > 0 || opportunities.length > 0) ? (
                <>
                  {activePolicies.length > 0 && (
                    <>
                      <div className="flex items-center justify-between px-4 py-[6px]">
                        <span className="text-2xs font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
                          Policies in force
                        </span>
                        {totalPremium > 0 && (
                          <span className="text-2xs font-semibold text-primary-gray-light-500">
                            ${totalPremium.toLocaleString(undefined, { maximumFractionDigits: 0 })} total
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 px-4 pb-3">
                        {activePolicies.map((p) => {
                          const exp = p.expiration_date ? expiryDisplay(p.expiration_date) : null;
                          return (
                            <div
                              key={p.policy_line_sk}
                              className="flex items-center justify-between text-xs"
                            >
                              <div className="flex min-w-0 flex-1 items-center gap-1">
                                <span className="font-medium text-primary-gray-light-900">
                                  {p.policy_type}
                                </span>
                                {p.expiring_premium != null && (
                                  <span className="text-xs text-primary-gray-light-500">
                                    · ${p.expiring_premium.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                  </span>
                                )}
                                <span className={`text-2xs font-semibold ${likelihoodTone(p.renewal_likelihood)}`}>
                                  {p.renewal_likelihood}
                                </span>
                              </div>
                              {exp && (
                                <div className={`ml-2 flex-shrink-0 text-xs ${exp.tone}`}>
                                  exp {exp.label}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {opportunities.length > 0 && (
                    <div className={`px-4 pb-3 ${activePolicies.length > 0 ? "border-t border-primary-gray-light-100 pt-[6px]" : "pt-[6px]"}`}>
                      <div className="mb-[6px] text-2xs font-bold uppercase tracking-[0.06em] text-primary-gray-light-400">
                        Opportunities
                      </div>
                      <div className="flex flex-col gap-1">
                        {opportunities.map((o) => (
                          <div
                            key={o.opportunityId}
                            className="flex items-start justify-between text-xs"
                          >
                            <div className="flex-1">
                              <span className="font-medium text-primary-gray-light-900">
                                {o.policyType}
                              </span>
                              {o.reasoning && (
                                <div className="mt-[1px] text-xs text-primary-gray-light-500">
                                  {o.reasoning}
                                </div>
                              )}
                            </div>
                            {o.estimatedPremium != null && (
                              <span className="ml-2 flex-shrink-0 text-xs font-semibold text-primary-success-600">
                                ~${o.estimatedPremium.toLocaleString()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PinnedHeader;
